
import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  CircularProgress,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
  Pagination,
  Paper,
  Avatar,
  Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaExternalLinkAlt, FaSearch, FaGoogle, FaMapMarkedAlt, FaUsers } from 'react-icons/fa';
import ApiService from '../../../services/api';
import CompetitorAnalysisService from '../../../services/CompetitorAnalysisService';
import logger from '../../../utils/logger';
import { formatDateTime } from '../../../utils/dateUtils';

// Styled components - Blue theme matching ClientListing
const Container = styled(Box)(({ theme }) => ({
  padding: '24px',
  backgroundColor: '#f5f7fa',
  minHeight: '100vh',
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
  flexWrap: 'wrap',
  gap: '16px',
}));

const TitleSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}));

const TitleIcon = styled(Box)(({ theme }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
}));

const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#fff',
    '&:hover fieldset': {
      borderColor: '#3b82f6',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3b82f6',
    },
  },
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '16px',
  marginBottom: '24px',
}));

const StatCard = styled(Card)(({ theme }) => ({
  padding: '20px',
  borderRadius: '16px',
  background: '#fff',
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
  },
}));

const StatIconBox = styled(Box)(({ bgcolor }) => ({
  width: '50px',
  height: '50px',
  borderRadius: '12px',
  background: bgcolor || 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  overflow: 'hidden',
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
}));

const HeaderCell = styled(TableCell)(({ theme }) => ({
  color: '#ffffff',
  fontWeight: 600,
  padding: '16px',
  fontSize: '14px',
  whiteSpace: 'nowrap',
}));

const DataCell = styled(TableCell)(({ theme }) => ({
  padding: '14px 16px',
  borderBottom: '1px solid #f0f0f0',
  fontSize: '14px',
}));

const FooterBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '24px',
  padding: '16px 20px',
  backgroundColor: '#fff',
  borderRadius: '16px',
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
}));

const GmbButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: '#ffffff',
  backgroundColor: '#10b981',
  textTransform: 'none',
  padding: '6px 12px',
  minWidth: 'auto',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: '#059669',
  },
  '&:disabled': {
    backgroundColor: '#d1d5db',
    color: '#9ca3af'
  }
}));

const HeatmapButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: '#ffffff',
  backgroundColor: '#ff6b35',
  textTransform: 'none',
  padding: '6px 12px',
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: '#e85a2a',
  },
  '&:disabled': {
    backgroundColor: '#d1d5db',
    color: '#9ca3af'
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: '#ffffff',
  backgroundColor: '#3b82f6',
  textTransform: 'none',
  padding: '6px 12px',
  minWidth: 'auto',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: '#2563eb',
  },
}));

const SearchTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    '& fieldset': {
      borderColor: '#e0e0e0',
    },
    '&:hover fieldset': {
      borderColor: '#0b91d6',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0b91d6',
    },
  },
}));

// Format status chip
const renderStatusChip = (status) => {
  const isActive = status?.toLowerCase() === 'active' || status?.toLowerCase() === 'approved';
  const isPending = status?.toLowerCase() === 'pending';

  return (
    <Chip
      label={status || 'N/A'}
      size="small"
      variant="outlined"
      sx={{
        fontWeight: 600,
        borderWidth: 1,
        color: isActive ? '#2ecc71' : isPending ? '#f39c12' : '#e74c3c',
        borderColor: isActive ? '#2ecc71' : isPending ? '#f39c12' : '#e74c3c',
        bgcolor: isActive ? 'rgba(46, 204, 113, 0.15)' : isPending ? 'rgba(243, 156, 18, 0.15)' : 'rgba(231, 76, 60, 0.06)'
      }}
    />
  );
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return 'N/A';
  }
};

// Get score color based on value (same logic as ClientScores.jsx)
const getScoreColor = (score) => {
  if (score === null || score === undefined) return 'default';
  if (score >= 250) return 'success';
  if (score >= 200) return 'info';
  if (score >= 150) return 'warning';
  return 'error';
};

// Get score label based on value
const getScoreLabel = (score) => {
  if (score === null || score === undefined) return 'N/A';
  if (score >= 250) return 'Excellent';
  if (score >= 200) return 'Good';
  if (score >= 150) return 'Needs Work';
  return 'Poor';
};

// Get competitor score color based on value (out of 1000)
const getCompetitorScoreColor = (score) => {
  if (score === null || score === undefined) return 'default';
  if (score >= 800) return 'success';
  if (score >= 600) return 'info';
  if (score >= 400) return 'warning';
  return 'error';
};

// Get competitor score label based on value
const getCompetitorScoreLabel = (score) => {
  if (score === null || score === undefined) return 'N/A';
  if (score >= 800) return 'Excellent';
  if (score >= 600) return 'Good';
  if (score >= 400) return 'Average';
  return 'Needs Improvement';
};

const Users = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const usersPerPage = 10;

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    // Filter users based on search query across ALL users
    if (searchQuery.trim() === '') {
      setFilteredUsers(users);
      setTotalUsers(users.length);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = users.filter(user =>
        user.name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        user.phone_number?.toLowerCase().includes(query) ||
        user.account_type?.toLowerCase().includes(query) ||
        user.status?.toLowerCase().includes(query)
      );
      setFilteredUsers(filtered);
      setTotalUsers(filtered.length);
      setPage(1); // Reset to first page when searching
    }
  }, [searchQuery, users]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      logger.log('📋 [Users] Fetching registered users...');
      const response = await ApiService.get('/admin/users/get-users');
      logger.log('✅ [Users] API Response:', response);

      // Extract users from response
      const userData = response?.data?.users || response?.users || [];
      logger.log(`✅ [Users] Found ${userData.length} users`);

      setUsers(userData);
      setFilteredUsers(userData);
      setTotalUsers(userData.length);
    } catch (err) {
      logger.error('❌ [Users] Error fetching users:', err);
      setError('Failed to fetch users. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Get current page users
  const indexOfLastUser = page * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(totalUsers / usersPerPage);

  const handleViewDetails = (user) => {
    navigate(`/admin-dashboard/user-view/${user.id}`);
  };

  const handleGoToGmbProfile = async (user) => {
    try {
      logger.log('🚀 [Users] Navigating to GMB profile for user:', user);

      // First, fetch user's GMB accounts to verify they have data
      const userDetails = await ApiService.get(`/admin/users/get-user/${user.id}`);
      const gmbAccounts = userDetails?.data?.gmb_accounts || [];
      const userData = userDetails?.data?.user || {};

      if (gmbAccounts.length === 0) {
        alert('This user has no GMB accounts connected yet.');
        return;
      }

      logger.log(`✅ [Users] Found ${gmbAccounts.length} GMB accounts for user`);

      // Attempt to resolve a usable Google access token from backend (handles refresh)
      let googleAccessToken = userData.google_access_token;
      try {
        const tokenResp = await ApiService.get(`/gmb/token?userId=${encodeURIComponent(user.id)}`);
        googleAccessToken = tokenResp?.data?.accessToken || tokenResp?.accessToken || googleAccessToken;
        if (googleAccessToken) {
          logger.log('🔑 [Users] Resolved Google access token for admin view via backend');
        } else {
          logger.warn('⚠️ [Users] Unable to resolve Google access token via backend; falling back to stored token');
        }
      } catch (tokenErr) {
        logger.warn('[Users] Backend token resolution failed:', tokenErr?.message || tokenErr);
      }

      // Prepare first GMB account as default profile for the NEW tab
      let defaultProfileData = null;
      if (gmbAccounts.length > 0) {
        const firstAccount = gmbAccounts[0];
        const locationData = typeof firstAccount.location_data === 'string'
          ? JSON.parse(firstAccount.location_data)
          : firstAccount.location_data;

        defaultProfileData = {
          id: firstAccount.location_id,
          locationId: firstAccount.location_id,
          accountId: firstAccount.account_id,
          name: locationData?.title || locationData?.name || 'Business',
          address: locationData?.storefrontAddress?.formattedAddress || 'Address not available',
          locationData: locationData,
          account: {
            name: `accounts/${firstAccount.account_id}`,
            accountName: user.name,
            type: 'PERSONAL'
          }
        };

        logger.log('🎯 [Users] Prepared default profile:', defaultProfileData.name);
      }

      // Store admin access data in sessionStorage (will be available in the new tab)
      const ownerEmailFromGmb = (gmbAccounts && gmbAccounts[0] && (gmbAccounts[0].user?.email || gmbAccounts[0].email)) || user.email;
      const adminAccessData = {
        isAdminAccess: true,
        originalAdminId: localStorage.getItem('userId'),
        targetUserId: user.id,
        targetUserEmail: ownerEmailFromGmb,
        targetUserName: user.name,
        gmbAccounts: gmbAccounts,
        googleAccessToken: googleAccessToken,
        defaultProfile: defaultProfileData, // Include profile data here
        timestamp: Date.now()
      };

      // IMPORTANT: Store in a temporary key that the new tab will read
      // This prevents overwriting admin's current session data
      const tempKey = `admin_viewing_user_${user.id}_${Date.now()}`;
      const serializedAccess = JSON.stringify(adminAccessData);

      // Persist to sessionStorage for this tab
      sessionStorage.setItem(tempKey, serializedAccess);
      sessionStorage.setItem('adminAccessData', serializedAccess);
      sessionStorage.setItem('adminViewingUserId', String(user.id));

      // Mirror into localStorage so the newly opened tab can hydrate then clean up
      try {
        localStorage.setItem(tempKey, serializedAccess);
        localStorage.setItem('latestAdminAccessTempKey', tempKey);
      } catch (storageError) {
        logger.warn('[Users] Unable to mirror admin access payload to localStorage:', storageError);
      }

      logger.log('✅ [Users] Admin access data stored in sessionStorage (NOT localStorage to preserve admin session)');

      // Open user's dashboard in a new tab with admin view
      const dashboardUrl = `/dashboard?userId=${user.id}&adminAccess=true&tempKey=${tempKey}`;
      window.open(dashboardUrl, '_blank');

      logger.log('✅ [Users] Opened GMB profile in new tab with', gmbAccounts.length, 'accounts');
      logger.log('🔒 [Users] Admin localStorage preserved - no logout will occur');
    } catch (err) {
      logger.error('❌ [Users] Error navigating to GMB profile:', err);
      alert('Failed to access user profile. Please try again.');
    }
  };

  const handleOpenHeatmap = async (user) => {
    try {
      logger.log('🗺️ [Admin-Users] Opening heatmap for user:', user);

      // Get user's GMB accounts to find business name and location_id
      const userDetails = await ApiService.get(`/admin/users/get-user/${user.id}`);
      const gmbAccounts = userDetails?.data?.gmb_accounts || [];

      if (gmbAccounts.length === 0) {
        alert('This user has no GMB accounts connected. Cannot fetch heatmap data.');
        return;
      }

      const firstAccount = gmbAccounts[0];
      const locationId = firstAccount.location_id;
      const locationData = typeof firstAccount.location_data === 'string'
        ? JSON.parse(firstAccount.location_data)
        : firstAccount.location_data;

      const businessName = locationData?.title || locationData?.name;

      if (!locationId && !businessName) {
        alert('Unable to determine location for this user.');
        return;
      }

      logger.log(`🔍 [Admin-Users] Fetching latest competitor analysis for location: ${locationId}, business: ${businessName}`);

      // Fetch using location_id for accurate user-specific lookup
      let response = null;
      if (locationId) {
        response = await CompetitorAnalysisService.getLatestByLocation(user.id, locationId);
      } else {
        // Fallback to business_name
        response = await CompetitorAnalysisService.getLatest(user.id, businessName);
      }

      if (response.success && response.data) {
        const analysisData = response.data;
        logger.log('✅ [Admin-Users] Found latest analysis:', analysisData);

        // Parse competitors_data which contains the grid/heatmap information
        let competitorsData = null;
        if (analysisData.competitors_data) {
          competitorsData = typeof analysisData.competitors_data === 'string'
            ? JSON.parse(analysisData.competitors_data)
            : analysisData.competitors_data;
        }

        // Check if we have grid data
        if (competitorsData && competitorsData.gridData && competitorsData.gridData.results) {
          logger.log('✅ [Admin-Users] Found grid data with', competitorsData.gridData.results.length, 'points');

          // Open heatmap results in a new tab with the analysis ID
          const heatmapUrl = `/heatmap-results?analysisId=${analysisData.id}&userId=${user.id}&adminView=true`;
          window.open(heatmapUrl, '_blank');

          logger.log('✅ [Admin-Users] Opened heatmap in new tab');
        } else {
          logger.warn('⚠️ [Admin-Users] No grid data found in latest analysis');
          alert(`No heatmap data found for ${businessName}. The user needs to run a competitor analysis first.`);
        }
      } else {
        logger.warn('⚠️ [Admin-Users] No competitor analysis found for this user');
        alert(`No competitor analysis found for ${businessName}. The user needs to run an analysis first.`);
      }
    } catch (error) {
      logger.error('❌ [Admin-Users] Error opening heatmap:', error);
      alert('Failed to open heatmap. Please try again.');
    }
  };

  return (
    <Container>
      <Header>
        <TitleSection>
          <TitleIcon>
            <FaUsers size={24} />
          </TitleIcon>
          <Box>
            <Typography variant="h4" fontWeight={700} color="#1f2937">User Management</Typography>
            <Typography variant="body2" color="text.secondary">View and manage all registered users</Typography>
          </Box>
        </TitleSection>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <SearchField
            placeholder="Search users..."
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch style={{ color: '#3b82f6' }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 320 }}
          />
        </Box>
      </Header>

      {/* Search Bar */}
      <Box mb={3}>
        <SearchTextField
          fullWidth
          size="small"
          placeholder="Search by name, email, phone, account type, or status..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaSearch style={{ color: '#9ca3af', fontSize: '14px' }} />
              </InputAdornment>
            ),
          }}
        />
        {searchQuery && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Found {totalUsers} user(s) matching "{searchQuery}"
          </Typography>
        )}
      </Box>

      <StyledTableContainer>
        <Table>
          <StyledTableHead>
            <TableRow>
              <HeaderCell>S.No</HeaderCell>
              <HeaderCell>Name</HeaderCell>
              <HeaderCell>Email</HeaderCell>
              <HeaderCell>Phone</HeaderCell>
              <HeaderCell>Account Type</HeaderCell>
              <HeaderCell>GMB Profiles & Plans</HeaderCell>
              <HeaderCell>Optimization Score</HeaderCell>
              <HeaderCell>Competitor Score</HeaderCell>
              <HeaderCell>Status</HeaderCell>
              <HeaderCell>Last Login</HeaderCell>
              <HeaderCell>Actions</HeaderCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <DataCell colSpan={11} style={{ textAlign: 'center', padding: '40px' }}>
                  <CircularProgress size={32} />
                  <Typography variant="body2" style={{ marginTop: '16px' }}>
                    Loading users...
                  </Typography>
                </DataCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <DataCell colSpan={11} style={{ textAlign: 'center', color: '#e74c3c', padding: '40px' }}>
                  <Typography variant="body1">{error}</Typography>
                  <Button
                    onClick={fetchUsers}
                    variant="outlined"
                    size="small"
                    style={{ marginTop: '16px' }}
                  >
                    Retry
                  </Button>
                </DataCell>
              </TableRow>
            ) : currentUsers && currentUsers.length > 0 ? (
              currentUsers.map((user, index) => (
                <TableRow key={user.id || index} hover>
                  <DataCell>{indexOfFirstUser + index + 1}</DataCell>
                  <DataCell>
                    <Typography variant="body2" fontWeight={500}>
                      {user.name || 'N/A'}
                    </Typography>
                  </DataCell>
                  <DataCell>{user.email || 'N/A'}</DataCell>
                  <DataCell>{user.phone_number || 'N/A'}</DataCell>
                  <DataCell>
                    <Chip
                      label={user.account_type || 'client'}
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: 'capitalize', fontSize: '11px', fontWeight: 500 }}
                    />
                  </DataCell>
                  <DataCell>
                    {user.gmb_profiles && user.gmb_profiles.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {user.gmb_profiles.map((profile, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <FaGoogle style={{ fontSize: '10px', color: '#4285f4' }} />
                            <Typography variant="caption" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {profile.business_name || profile.location_name || 'Business'}
                            </Typography>
                            <Chip
                              label={profile.plan ? profile.plan.toUpperCase() : 'No Plan'}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '10px',
                                fontWeight: 600,
                                ml: 0.5,
                                bgcolor: profile.plan === 'pro' ? '#1e40af' :
                                  profile.plan === 'advance' ? '#3b82f6' :
                                    profile.plan === 'basic' ? '#22c55e' : '#e5e7eb',
                                color: profile.plan ? '#fff' : '#6b7280'
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Chip
                        label={user.has_gmb_access ? 'Connected (No Profiles)' : 'No GMB'}
                        size="small"
                        color={user.has_gmb_access ? 'info' : 'default'}
                        variant="outlined"
                      />
                    )}
                  </DataCell>
                  <DataCell>
                    {user.optimization_score ? (
                      <Tooltip title={`Score checked: ${formatDate(user.optimization_score.checked_at)}`}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            label={`${user.optimization_score.total_score} / 300`}
                            size="small"
                            color={getScoreColor(user.optimization_score.total_score)}
                            sx={{ fontWeight: 600, fontSize: '11px' }}
                          />
                          <Typography variant="caption" color="textSecondary" sx={{ fontSize: '10px' }}>
                            {getScoreLabel(user.optimization_score.total_score)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      <Chip
                        label="Not Checked"
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '10px', color: '#9ca3af' }}
                      />
                    )}
                  </DataCell>
                  <DataCell>
                    {user.competitor_score ? (
                      <Tooltip title={`Keyword: "${user.competitor_score.keyword}" | Rank: #${user.competitor_score.rank} | Checked: ${formatDate(user.competitor_score.checked_at)}`}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            label={`${Math.round(user.competitor_score.score)} / 1000`}
                            size="small"
                            color={getCompetitorScoreColor(user.competitor_score.score)}
                            sx={{ fontWeight: 600, fontSize: '11px' }}
                          />
                          <Typography variant="caption" color="textSecondary" sx={{ fontSize: '10px' }}>
                            {getCompetitorScoreLabel(user.competitor_score.score)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      <Chip
                        label="Not Analyzed"
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '10px', color: '#9ca3af' }}
                      />
                    )}
                  </DataCell>
                  <DataCell>{renderStatusChip(user.status)}</DataCell>
                  <DataCell>
                    <Typography variant="caption" color="textSecondary">
                      {formatDate(user.last_login)}
                    </Typography>
                  </DataCell>
                  <DataCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="View Details">
                        <ActionButton onClick={() => handleViewDetails(user)}>
                          <FaEye style={{ marginRight: '4px' }} />
                          View
                        </ActionButton>
                      </Tooltip>
                      <Tooltip title={user.has_gmb_access ? "Access User's GMB Profile" : "User has no GMB access"}>
                        <span>
                          <GmbButton
                            onClick={() => handleGoToGmbProfile(user)}
                            disabled={!user.has_gmb_access}
                            size="small"
                          >
                            <FaExternalLinkAlt style={{ marginRight: '4px', fontSize: '10px' }} />
                            Go to GMB Profile
                          </GmbButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </DataCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <DataCell colSpan={11} style={{ textAlign: 'center', padding: '40px' }}>
                  <Typography variant="body1" color="textSecondary">
                    {searchQuery ? `No users found matching "${searchQuery}"` : 'No users found'}
                  </Typography>
                </DataCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <FooterBox>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Showing {filteredUsers.length === 0 ? 0 : (Math.min(indexOfFirstUser + 1, filteredUsers.length))} - {filteredUsers.length === 0 ? 0 : Math.min(indexOfLastUser, filteredUsers.length)} of {filteredUsers.length} users
          </Typography>
          <Stack spacing={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              showFirstButton
              showLastButton
            />
          </Stack>
        </FooterBox>
      )}
    </Container>
  );
};

export default Users;
